import { forwardRef, useReducer } from "react";
import { Input } from "../ui/input";
import { cn } from "@/utils/classnames";

// Formatter for integers
const integerFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// Custom IntegerInput component
interface IntegerInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  className?: string;
  min?: number;
  max?: number;
  error?: boolean;
  [key: string]: any;
}

const IntegerInput = forwardRef<HTMLInputElement, IntegerInputProps>(
  ({ className, value, onChange, min, max, error, ...props }, ref) => {
    const initialValue =
      value !== undefined
        ? integerFormatter.format(value)
        : min !== undefined
        ? integerFormatter.format(min)
        : "";

    const [input, setInput] = useReducer((_, next: string) => {
      const digits = next.replace(/\D/g, "");
      return digits
        ? integerFormatter.format(Number(digits))
        : min !== undefined
        ? integerFormatter.format(min)
        : "";
    }, initialValue);

    function handleChange(
      realChangeFn: (value: number | undefined) => void,
      formattedValue: string
    ) {
      const digits = formattedValue.replace(/\D/g, "");
      const realValue = digits
        ? Number(digits)
        : min !== undefined
        ? min
        : undefined;
      realChangeFn(realValue);
    }

    return (
      <div className="flex">
        <Input
          ref={ref}
          value={input}
          onChange={(ev) => {
            setInput(ev.target.value);
            handleChange(onChange, ev.target.value);
          }}
          className={cn(className, error && "border-red-500")}
          placeholder="0"
          {...props}
        />
      </div>
    );
  }
);
IntegerInput.displayName = "IntegerInput";

export default IntegerInput;
